import {ColumnDef} from "@tanstack/react-table"
import {useMemo} from "react"
import {useLocation} from "react-router-dom"
import {sortableColumnHeader} from "@/components/data-table.tsx"
import {Text} from "@/components/tailwind/text"
import {Link} from "@/components/tailwind/link"
import {BankDataValidationEntry} from "@/models/bank-data-validation.ts"
import {ArrowTopRightOnSquareIcon} from "@heroicons/react/16/solid"
import i18next from "i18next"

function translateFinding(finding: string): string {
    return i18next.t(`bankDataValidation.findings.${finding}`, {ns: "reports", defaultValue: finding})
}

export function useBankDataValidationColumns(): ColumnDef<BankDataValidationEntry>[] {
    const location = useLocation()
    
    return useMemo(() => {
        const returnUrl = encodeURIComponent(`/reports/bank-data-validation${location.search}`)
        
        return [
            {
                accessorKey: "memberNo",
                header: ({column}) =>
                    sortableColumnHeader(column, "bankDataValidation.table.headers.memberNo", "reports"),
            },
            {
                accessorKey: "lastName",
                header: ({column}) =>
                    sortableColumnHeader(column, "bankDataValidation.table.headers.lastName", "reports"),
            },
            {
                accessorKey: "firstName",
                header: ({column}) =>
                    sortableColumnHeader(column, "bankDataValidation.table.headers.firstName", "reports"),
            },
            {
                id: "findings",
                accessorFn: (row) => row.findings.map(translateFinding).join(", "),
                header: ({column}) =>
                    sortableColumnHeader(column, "bankDataValidation.table.headers.findings", "reports"),
                cell: ({row}) => {
                    const findings = row.original.findings
                    return (
                        <Text>
                            {findings.map(translateFinding).join(", ")}
                        </Text>
                    )
                },
            },
            {
                id: "details",
                header: () => null,
                cell: ({row}) => {
                    const memberId = row.original.memberId
                    return (
                        <Link
                            href={`${import.meta.env.BASE_URL}members/${memberId}?returnUrl=${returnUrl}`}
                            data-testid={`bank-validation-details-${row.original.memberNo}`}
                            aria-label={i18next.t("bankDataValidation.viewDetailsAriaLabel", {ns: "reports", memberNo: row.original.memberNo})}
                        >
                            <ArrowTopRightOnSquareIcon className="size-4"/>
                        </Link>
                    )
                },
            },
        ]
    }, [location.search])
}

export function useBankDataValidationColumnsMissingAccount(): ColumnDef<BankDataValidationEntry>[] {
    const location = useLocation()
    
    return useMemo(() => {
        const returnUrl = encodeURIComponent(`/reports/bank-data-validation${location.search}`)
        
        return [
            {
                accessorKey: "memberNo",
                header: ({column}) =>
                    sortableColumnHeader(column, "bankDataValidation.table.headers.memberNo", "reports"),
            },
            {
                accessorKey: "lastName",
                header: ({column}) =>
                    sortableColumnHeader(column, "bankDataValidation.table.headers.lastName", "reports"),
            },
            {
                accessorKey: "firstName",
                header: ({column}) =>
                    sortableColumnHeader(column, "bankDataValidation.table.headers.firstName", "reports"),
            },
            {
                id: "details",
                header: () => null,
                cell: ({row}) => {
                    const memberId = row.original.memberId
                    return (
                        <Link
                            href={`${import.meta.env.BASE_URL}members/${memberId}?returnUrl=${returnUrl}`}
                            data-testid={`bank-validation-details-${row.original.memberNo}`}
                            aria-label={i18next.t("bankDataValidation.viewDetailsAriaLabel", {ns: "reports", memberNo: row.original.memberNo})}
                        >
                            <ArrowTopRightOnSquareIcon className="size-4"/>
                        </Link>
                    )
                },
            },
        ]
    }, [location.search])
}

// Keep old exports for backward compatibility (e.g., tests)
export const bankDataValidationColumns: ColumnDef<BankDataValidationEntry>[] = [
    {
        accessorKey: "memberNo",
        header: ({column}) =>
            sortableColumnHeader(column, "bankDataValidation.table.headers.memberNo", "reports"),
    },
    {
        accessorKey: "lastName",
        header: ({column}) =>
            sortableColumnHeader(column, "bankDataValidation.table.headers.lastName", "reports"),
    },
    {
        accessorKey: "firstName",
        header: ({column}) =>
            sortableColumnHeader(column, "bankDataValidation.table.headers.firstName", "reports"),
    },
    {
        id: "findings",
        accessorFn: (row) => row.findings.map(translateFinding).join(", "),
        header: ({column}) =>
            sortableColumnHeader(column, "bankDataValidation.table.headers.findings", "reports"),
        cell: ({row}) => {
            const findings = row.original.findings
            return (
                <Text>
                    {findings.map(translateFinding).join(", ")}
                </Text>
            )
        },
    },
    {
        id: "details",
        header: () => null,
        cell: ({row}) => {
            const memberId = row.original.memberId
            return (
                <Link
                    href={`${import.meta.env.BASE_URL}members/${memberId}?from=bank-data-validation`}
                    data-testid={`bank-validation-details-${row.original.memberNo}`}
                    aria-label={i18next.t("bankDataValidation.viewDetailsAriaLabel", {ns: "reports", memberNo: row.original.memberNo})}
                >
                    <ArrowTopRightOnSquareIcon className="size-4"/>
                </Link>
            )
        },
    },
]

export const bankDataValidationColumnsMissingAccount: ColumnDef<BankDataValidationEntry>[] = [
    {
        accessorKey: "memberNo",
        header: ({column}) =>
            sortableColumnHeader(column, "bankDataValidation.table.headers.memberNo", "reports"),
    },
    {
        accessorKey: "lastName",
        header: ({column}) =>
            sortableColumnHeader(column, "bankDataValidation.table.headers.lastName", "reports"),
    },
    {
        accessorKey: "firstName",
        header: ({column}) =>
            sortableColumnHeader(column, "bankDataValidation.table.headers.firstName", "reports"),
    },
    {
        id: "details",
        header: () => null,
        cell: ({row}) => {
            const memberId = row.original.memberId
            return (
                <Link
                    href={`${import.meta.env.BASE_URL}members/${memberId}?from=bank-data-validation`}
                    data-testid={`bank-validation-details-${row.original.memberNo}`}
                    aria-label={i18next.t("bankDataValidation.viewDetailsAriaLabel", {ns: "reports", memberNo: row.original.memberNo})}
                >
                    <ArrowTopRightOnSquareIcon className="size-4"/>
                </Link>
            )
        },
    },
]
